import { useState, useEffect } from "react";
import axios from "axios";
import { User, Lock, Building2, Shield, Plus, Trash2, Edit, Camera, Save, Eye, EyeOff, CheckCircle, Loader2, Globe } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import ImageCropperModal from "../../components/ImageCropperModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGENT_PERMISSIONS = [
  { id: "quotes", label: "Manage Quotes" },
  { id: "bookings", label: "Manage Bookings" },
  { id: "payments", label: "Manage Payments" },
  { id: "reports", label: "View Reports" },
];


const AgentSettings = () => {
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  const [profileForm, setProfileForm] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    address: "",
    city: "",
    district: "",
    state: "",
    pin_code: "",
    website: "",
    gst_number: "",
    pan_number: "",
    logo_url: "",
    profile_picture_url: "",
    designation: "",
    birthday: "",
    is_gst_registered: true,
    gst_verified: false,
    pan_verified: false
  });

  const [verifyingGst, setVerifyingGst] = useState(false);
  const [verifyingPan, setVerifyingPan] = useState(false);
  const [gstResult, setGstResult] = useState(null);
  const [panResult, setPanResult] = useState(null);

  const [employeeForm, setEmployeeForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    designation: "Staff",
    status: "active",
    permissions: [],
    birthday: ""
  });

  const [profilePicture, setProfilePicture] = useState(
    localStorage.getItem("agentProfilePic") || ""
  );
  // Cropper state
  const [cropper, setCropper] = useState({ open: false, file: null, target: null });

  useEffect(() => {
    fetchProfile();
    // Only fetch staff if user is the main agent (has no parent)
    // Actually, backend restricts it, so we can try fetching.
    // If user is staff, this might fail or return empty.
    fetchEmployees();
  }, []);


  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileData(response.data);
      setProfileForm({
        name: response.data.name || "",
        email: response.data.email || "",
        phone: response.data.phone || "",
        company_name: response.data.company_name || "",
        address: response.data.address || "",
        city: response.data.city || "",
        district: response.data.district || "",
        state: response.data.state || "",
        pin_code: response.data.pin_code || "",
        website: response.data.website || "",
        gst_number: response.data.gst_number || "",
        pan_number: response.data.pan_number || "",
        logo_url: response.data.logo_url || "",
        profile_picture_url: response.data.profile_picture_url || localStorage.getItem("agentProfilePic") || "",
        designation: response.data.designation || "",
        birthday: response.data.birthday || "",
        is_gst_registered: response.data.is_gst_registered !== false,
        gst_verified: response.data.gst_verified || false,
        pan_verified: response.data.pan_verified || false
      });
      if (response.data.profile_picture_url) {
        setProfilePicture(response.data.profile_picture_url);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/agent/staff`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(res.data);
    } catch (error) {
      console.error("Error fetching staff:", error);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Password changed successfully!");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/auth/me/profile`, profileForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile updated successfully!");
      fetchProfile();
      if (refreshUser) await refreshUser();
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    }
  };

  const verifyGST = async () => {
    if (!profileForm.gst_number || profileForm.gst_number.length < 15) {
      toast.error("Please enter a valid 15-character GSTIN");
      return;
    }
    setVerifyingGst(true);
    setGstResult(null);
    try {
      const res = await axios.post(`${API}/verify/gstin`, { gstin: profileForm.gst_number }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGstResult(res.data);
      if (res.data.valid) {
        setProfileForm(p => ({ ...p, gst_verified: true }));
        toast.success(`GST Verified! State: ${res.data.state_name}`);
      } else {
        toast.error(res.data.error);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally { setVerifyingGst(false); }
  };

  const verifyPAN = async () => {
    if (!profileForm.pan_number || profileForm.pan_number.length < 10) {
      toast.error("Please enter a valid 10-character PAN");
      return;
    }
    setVerifyingPan(true);
    setPanResult(null);
    try {
      const res = await axios.post(`${API}/verify/pan`, { pan: profileForm.pan_number }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPanResult(res.data);
      if (res.data.valid) {
        setProfileForm(p => ({ ...p, pan_verified: true }));
        toast.success(`PAN Verified! Type: ${res.data.holder_type}`);
      } else {
        toast.error(res.data.error);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally { setVerifyingPan(false); }
  };

  const handleEmployeeSubmit = async (e) => {
    e.preventDefault();
    if (!employeeForm.name || !employeeForm.email) {
      toast.error("Name and Email are required");
      return;
    }
    if (!editingEmployee && !employeeForm.password) {
      toast.error("Password is required for new staff");
      return;
    }

    try {
      if (editingEmployee) {
        await axios.put(`${API}/agent/staff/${editingEmployee.id}`, employeeForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Staff updated!");
      } else {
        await axios.post(`${API}/agent/staff`, employeeForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Staff added!");
      }
      setShowEmployeeModal(false);
      resetEmployeeForm();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save staff");
    }
  };

  const deleteEmployee = async (id) => {
    if (!window.confirm("Delete this staff member?")) return;
    try {
      await axios.delete(`${API}/agent/staff/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Staff deleted!");
      fetchEmployees();
    } catch (error) {
      toast.error("Failed to delete staff");
    }
  };

  const resetEmployeeForm = () => {
    setEditingEmployee(null);
    setEmployeeForm({
      name: "",
      email: "",
      password: "",
      phone: "",
      designation: "Staff",
      status: "active",
      permissions: [],
      birthday: ""
    });
  };

  const openEditEmployee = (emp) => {
    setEditingEmployee(emp);
    setEmployeeForm({
      name: emp.name,
      email: emp.email,
      password: "", 
      phone: emp.phone || "",
      designation: emp.designation || "Staff",
      status: emp.status || "active",
      permissions: emp.permissions || [],
      birthday: emp.birthday || ""
    });
    setShowEmployeeModal(true);
  };

  const togglePermission = (permId) => {
    setEmployeeForm(prev => {
      if (prev.permissions.includes(permId)) {
        return { ...prev, permissions: prev.permissions.filter(p => p !== permId) };
      } else {
        return { ...prev, permissions: [...prev.permissions, permId] };
      }
    });
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image size should be less than 5MB"); e.target.value = ""; return; }
    setCropper({ open: true, file, target: "profile_picture" });
    e.target.value = "";
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image size should be less than 5MB"); e.target.value = ""; return; }
    setCropper({ open: true, file, target: "logo" });
    e.target.value = "";
  };

  const handleCroppedSave = (dataUrl) => {
    if (cropper.target === "profile_picture") {
      setProfilePicture(dataUrl);
      localStorage.setItem("agentProfilePic", dataUrl);
      setProfileForm(prev => ({ ...prev, profile_picture_url: dataUrl }));
      toast.success("Profile picture updated! Click 'Save Changes' to persist.");
    } else if (cropper.target === "logo") {
      setProfileForm(prev => ({ ...prev, logo_url: dataUrl }));
      toast.success("Logo updated! Click 'Save Changes' to persist.");
    }
    setCropper({ open: false, file: null, target: null });
  };


  const getInitials = (name) => {
    if (!name) return "AG";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <DashboardLayout type="agent">
      <div className="max-w-4xl mx-auto space-y-6" data-testid="agent-settings">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Settings</h1>
          <p className="text-gray-500">Manage your account settings and preferences</p>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-gray-50">
            <TabsTrigger value="profile" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
              <User className="w-4 h-4 mr-2" /> Profile
            </TabsTrigger>
            <TabsTrigger value="company" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
              <Building2 className="w-4 h-4 mr-2" /> Company
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
              <Shield className="w-4 h-4 mr-2" /> Users & Access
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
              <Lock className="w-4 h-4 mr-2" /> Security
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-gray-800">Profile Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Profile Picture */}
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <Avatar className="w-24 h-24 border-4 border-primary-700">
                      <AvatarImage src={profilePicture} />
                      <AvatarFallback className="bg-primary-700 text-gray-800 text-2xl">
                        {getInitials(profileData?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <label 
                      htmlFor="profile-pic" 
                      className="absolute bottom-0 right-0 w-8 h-8 bg-secondary-500 rounded-full flex items-center justify-center cursor-pointer hover:bg-secondary-600 transition-colors"
                    >
                      <Camera className="w-4 h-4 text-gray-800" />
                    </label>
                    <input 
                      type="file" 
                      id="profile-pic" 
                      accept="image/*" 
                      onChange={handleProfilePictureChange}
                      className="hidden" 
                    />
                  </div>
                  <div>
                    <h3 className="text-gray-800 font-semibold text-lg">{profileData?.name}</h3>
                    <p className="text-gray-500">{profileData?.email}</p>
                    <p className="text-[11px] text-gray-400 mt-1">Displays at <strong>56 × 56 px</strong> on dashboard &amp; in PDF signature. Recommended upload: 400 × 400 px (square).</p>
                  </div>
                </div>

                <form onSubmit={handleProfileUpdate} className="grid md:grid-cols-2 gap-6 pt-6 border-t border-gray-200">
                  <div>
                    <Label className="text-gray-500">Full Name</Label>
                    <Input value={profileForm.name} onChange={e => setProfileForm({...profileForm, name: e.target.value})} className="bg-gray-50 border-gray-200 text-gray-800 mt-1" />
                  </div>
                  <div>
                    <Label className="text-gray-500">Email Address</Label>
                    <Input value={profileForm.email} disabled className="bg-gray-50/50 border-gray-200 text-gray-500 mt-1" />
                  </div>
                  <div>
                    <Label className="text-gray-500">Phone Number</Label>
                    <Input value={profileForm.phone} onChange={e => setProfileForm({...profileForm, phone: e.target.value})} className="bg-gray-50 border-gray-200 text-gray-800 mt-1" />
                  </div>
                  <div>
                    <Label className="text-gray-500">Designation</Label>
                    <Input value={profileForm.designation} onChange={e => setProfileForm({...profileForm, designation: e.target.value})} placeholder="e.g. Owner, Travel Consultant" className="bg-gray-50 border-gray-200 text-gray-800 mt-1" data-testid="agent-designation-input" />
                  </div>
                  <div>
                    <Label className="text-gray-500">Birthday</Label>
                    <Input type="date" value={profileForm.birthday} onChange={e => setProfileForm({...profileForm, birthday: e.target.value})} className="bg-gray-50 border-gray-200 text-gray-800 mt-1" />
                  </div>
                  <div className="md:col-span-2">
                    <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">Save Changes</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Company Tab */}
          <TabsContent value="company">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-gray-800">Company Details</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProfileUpdate} className="space-y-6">
                    <div className="flex items-center gap-6 mb-6">
                        <div className="relative group">
                        <Avatar className="w-24 h-24 border-4 border-gray-200">
                            <AvatarImage src={profileForm.logo_url} />
                            <AvatarFallback className="bg-gray-50 text-2xl font-bold">
                            {profileForm.company_name?.charAt(0) || "C"}
                            </AvatarFallback>
                        </Avatar>
                        <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 rounded-full cursor-pointer transition-opacity">
                            <Camera className="w-6 h-6 text-gray-800" />
                            <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
                        </label>
                        </div>
                        <div>
                        <h3 className="text-lg font-medium text-gray-800">Company Logo</h3>
                        <p className="text-sm text-gray-500">Upload your agency logo</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">Displays at <strong>96 × 96 px</strong> on card &amp; PDF header. Recommended upload: 512 × 512 px (square).</p>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <Label>Company Name</Label>
                            <Input value={profileForm.company_name} onChange={e => setProfileForm({...profileForm, company_name: e.target.value})} className="bg-gray-50 border-gray-200 mt-1" />
                        </div>
                        <div className="md:col-span-2 flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div>
                            <Label>GST Registered</Label>
                            <p className="text-[11px] text-gray-500">Turn OFF if your company is not GST registered</p>
                          </div>
                          <Switch
                            checked={profileForm.is_gst_registered}
                            onCheckedChange={v => setProfileForm({ ...profileForm, is_gst_registered: v, gst_number: v ? profileForm.gst_number : "", gst_verified: v ? profileForm.gst_verified : false })}
                            data-testid="agent-gst-toggle"
                          />
                        </div>
                        {profileForm.is_gst_registered && (
                        <div>
                            <Label>GST Number</Label>
                            <div className="flex gap-2 mt-1">
                              <div className="relative flex-1">
                                <Input
                                  value={profileForm.gst_number}
                                  onChange={e => { setProfileForm({...profileForm, gst_number: e.target.value.toUpperCase(), gst_verified: false}); setGstResult(null); }}
                                  className={`bg-gray-50 border-gray-200 uppercase font-mono ${profileForm.gst_verified ? 'border-emerald-400 pr-9' : ''}`}
                                  maxLength={15}
                                  placeholder="03BQJPS3148F2ZU"
                                  data-testid="agent-gst-input"
                                />
                                {profileForm.gst_verified && <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" data-testid="agent-gst-verified-icon" />}
                              </div>
                              <Button type="button" variant="outline" size="sm" onClick={verifyGST} disabled={verifyingGst || !profileForm.gst_number} className="h-10 px-3 text-xs whitespace-nowrap" data-testid="agent-verify-gst-btn">
                                {verifyingGst ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                              </Button>
                            </div>
                            {gstResult && gstResult.valid && (
                              <p className="text-xs text-emerald-600 mt-1" data-testid="agent-gst-verify-result">Verified - {gstResult.entity_type} | {gstResult.state_name}</p>
                            )}
                            {gstResult && !gstResult.valid && (
                              <p className="text-xs text-red-500 mt-1">{gstResult.error}</p>
                            )}
                        </div>
                        )}
                        <div>
                            <Label>PAN Number</Label>
                            <div className="flex gap-2 mt-1">
                              <div className="relative flex-1">
                                <Input
                                  value={profileForm.pan_number}
                                  onChange={e => { setProfileForm({...profileForm, pan_number: e.target.value.toUpperCase(), pan_verified: false}); setPanResult(null); }}
                                  className={`bg-gray-50 border-gray-200 uppercase font-mono ${profileForm.pan_verified ? 'border-emerald-400 pr-9' : ''}`}
                                  maxLength={10}
                                  placeholder="ABCDE1234F"
                                  data-testid="agent-pan-input"
                                />
                                {profileForm.pan_verified && <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" data-testid="agent-pan-verified-icon" />}
                              </div>
                              <Button type="button" variant="outline" size="sm" onClick={verifyPAN} disabled={verifyingPan || !profileForm.pan_number} className="h-10 px-3 text-xs whitespace-nowrap" data-testid="agent-verify-pan-btn">
                                {verifyingPan ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                              </Button>
                            </div>
                            {panResult && panResult.valid && (
                              <p className="text-xs text-emerald-600 mt-1" data-testid="agent-pan-verify-result">Verified - {panResult.holder_type}</p>
                            )}
                            {panResult && !panResult.valid && (
                              <p className="text-xs text-red-500 mt-1">{panResult.error}</p>
                            )}
                        </div>
                        <div className="md:col-span-2">
                            <Label>Address</Label>
                            <Input value={profileForm.address} onChange={e => setProfileForm({...profileForm, address: e.target.value})} className="bg-gray-50 border-gray-200 mt-1" />
                        </div>
                        <div>
                            <Label>City</Label>
                            <Input value={profileForm.city} onChange={e => setProfileForm({...profileForm, city: e.target.value})} className="bg-gray-50 border-gray-200 mt-1" />
                        </div>
                        <div>
                            <Label>District</Label>
                            <Input value={profileForm.district} onChange={e => setProfileForm({...profileForm, district: e.target.value})} className="bg-gray-50 border-gray-200 mt-1" data-testid="agent-district-input" />
                        </div>
                        <div>
                            <Label>State</Label>
                            <Input value={profileForm.state} onChange={e => setProfileForm({...profileForm, state: e.target.value})} className="bg-gray-50 border-gray-200 mt-1" />
                        </div>
                        <div>
                            <Label>Pin Code</Label>
                            <Input value={profileForm.pin_code} onChange={e => setProfileForm({...profileForm, pin_code: e.target.value})} className="bg-gray-50 border-gray-200 mt-1" data-testid="agent-pincode-input" />
                        </div>
                        <div className="md:col-span-2">
                            <Label>Website</Label>
                            <div className="relative mt-1">
                              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <Input value={profileForm.website} onChange={e => setProfileForm({...profileForm, website: e.target.value})} className="bg-gray-50 border-gray-200 pl-9" placeholder="www.example.com" data-testid="agent-website-input" />
                            </div>
                        </div>
                    </div>
                    <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">Save Company Details</Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab (Only for Owners/Admins?) Currently for any agent, assuming they are owner if they can access */}
          <TabsContent value="users">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-gray-800">Staff Management</CardTitle>
                <Button onClick={() => { resetEmployeeForm(); setShowEmployeeModal(true); }} className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" /> Add Staff
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm text-gray-500">Name</th>
                        <th className="px-4 py-3 text-left text-sm text-gray-500">Designation</th>
                        <th className="px-4 py-3 text-left text-sm text-gray-500">Status</th>
                        <th className="px-4 py-3 text-left text-sm text-gray-500">Permissions</th>
                        <th className="px-4 py-3 text-right text-sm text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {employees.map(emp => (
                        <tr key={emp.id} className="hover:bg-gray-50/30">
                          <td className="px-4 py-3">
                            <p className="text-gray-800 font-medium">{emp.name}</p>
                            <p className="text-xs text-gray-400">{emp.email}</p>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{emp.designation}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${emp.status === 'active' || emp.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                {emp.status === 'approved' ? 'Active' : emp.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                                {emp.permissions?.map(p => (
                                  <span key={p} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">
                                    {p}
                                  </span>
                                ))}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="ghost" size="sm" onClick={() => openEditEmployee(emp)}>
                                <Edit className="w-4 h-4 text-gray-500" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => deleteEmployee(emp.id)}>
                                <Trash2 className="w-4 h-4 text-red-400" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader><CardTitle className="text-gray-800">Change Password</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="max-w-md space-y-4">
                  <div>
                    <Label className="text-gray-600">Current Password</Label>
                    <Input type="password" value={passwordForm.current_password} onChange={e => setPasswordForm({...passwordForm, current_password: e.target.value})} className="bg-gray-50 border-gray-200" required />
                  </div>
                  <div>
                    <Label className="text-gray-600">New Password</Label>
                    <Input type="password" value={passwordForm.new_password} onChange={e => setPasswordForm({...passwordForm, new_password: e.target.value})} className="bg-gray-50 border-gray-200" required />
                  </div>
                  <div>
                    <Label className="text-gray-600">Confirm Password</Label>
                    <Input type="password" value={passwordForm.confirm_password} onChange={e => setPasswordForm({...passwordForm, confirm_password: e.target.value})} className="bg-gray-50 border-gray-200" required />
                  </div>
                  <Button type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">Change Password</Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* User Modal */}
        <Dialog open={showEmployeeModal} onOpenChange={setShowEmployeeModal}>
          <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEmployee ? "Edit Staff" : "Add New Staff"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEmployeeSubmit} className="space-y-4" autoComplete="off">
              {/* Dummy inputs to prevent browser autofill */}
              <input type="text" name="prevent_autofill" value="" onChange={() => {}} style={{display: 'none'}} />
              <input type="password" name="prevent_password_autofill" value="" onChange={() => {}} style={{display: 'none'}} />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input value={employeeForm.name} onChange={e => setEmployeeForm({...employeeForm, name: e.target.value})} className="bg-gray-50 border-gray-200" required autoComplete="off" data-testid="staff-name-input" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={employeeForm.email} onChange={e => setEmployeeForm({...employeeForm, email: e.target.value})} className="bg-gray-50 border-gray-200" required disabled={!!editingEmployee} autoComplete="off" name={`staff-email-${Date.now()}`} placeholder="staff@yourcompany.com" data-testid="staff-email-input" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input value={employeeForm.phone} onChange={e => setEmployeeForm({...employeeForm, phone: e.target.value})} className="bg-gray-50 border-gray-200" autoComplete="off" />
                </div>
                <div>
                  <Label>Designation</Label>
                  <Input value={employeeForm.designation} onChange={e => setEmployeeForm({...employeeForm, designation: e.target.value})} className="bg-gray-50 border-gray-200" placeholder="e.g. Sales" autoComplete="off" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label>Birthday</Label>
                    <Input type="date" value={employeeForm.birthday} onChange={e => setEmployeeForm({...employeeForm, birthday: e.target.value})} className="bg-gray-50 border-gray-200" />
                </div>
                <div className="flex items-end pb-2">
                  <div className="flex items-center space-x-2">
                    <Switch 
                      id="status-mode" 
                      checked={employeeForm.status === 'active'}
                      onCheckedChange={(checked) => setEmployeeForm(prev => ({ ...prev, status: checked ? 'active' : 'inactive' }))}
                    />
                    <Label htmlFor="status-mode" className={employeeForm.status === 'active' ? 'text-green-400' : 'text-gray-500'}>
                      {employeeForm.status === 'active' ? 'Active' : 'Inactive'}
                    </Label>
                  </div>
                </div>
              </div>

              {!editingEmployee && (
                <div>
                  <Label>Password *</Label>
                  <Input type="password" value={employeeForm.password} onChange={e => setEmployeeForm({...employeeForm, password: e.target.value})} className="bg-gray-50 border-gray-200" required autoComplete="new-password" name={`staff-password-${Date.now()}`} placeholder="Set a login password" data-testid="staff-password-input" />
                  <p className="text-[11px] text-gray-400 mt-1">Staff will log in with this password at the login page.</p>
                </div>
              )}

              <div className="p-3 bg-gray-50 rounded border border-gray-200">
                <Label className="mb-2 block text-emerald-600">Access Permissions</Label>
                <div className="grid grid-cols-2 gap-2">
                  {AGENT_PERMISSIONS.map(perm => (
                    <label key={perm.id} className="flex items-center space-x-2 cursor-pointer select-none" data-testid={`perm-${perm.id}`}>
                      <input 
                        type="checkbox"
                        checked={employeeForm.permissions.includes(perm.id)}
                        onChange={() => togglePermission(perm.id)}
                        className="h-4 w-4 rounded border-slate-400 text-emerald-600 focus:ring-emerald-500 accent-emerald-600 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">{perm.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">Save Staff</Button>
            </form>
          </DialogContent>
        </Dialog>

        <ImageCropperModal
          open={cropper.open}
          onClose={() => setCropper({ open: false, file: null, target: null })}
          onSave={handleCroppedSave}
          file={cropper.file}
          outputSize={cropper.target === "logo" ? 512 : 400}
          shape={cropper.target === "logo" ? "square" : "circle"}
          title={cropper.target === "logo" ? "Adjust Company Logo" : "Adjust Profile Picture"}
        />

      </div>
    </DashboardLayout>
  );
};

export default AgentSettings;
